import React, { useEffect, useRef, useState } from 'react'
import { reaction, comparer } from 'mobx'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import WidgetContainer from '../../WidgetContainer'
import Panel from '../../../../UniformComponents/Panel'
import EmptyStateImg from '../../../../assets/images/widget_preview.svg'
import Typography from '../../../../UniformComponents/Typography'

const WidgetPreview = ({ newWidget, dashStore }) => {
    const [chartData, setChartData] = useState()
    const [loadingData, setLoadingData] = useState(false)
    const { years } = dashStore
    const latestFetchId = useRef(0) // idx to batch re-render into one if both chartRefreshFields and chartType change

    useEffect(() => {
        const { getChartData } = dashStore
        const dispose = reaction(
            () => newWidget.chartRefreshFields,
            async () => {
                const widget = newWidget.newWidgetToWidget()
                if (!widget.questionId && !widget.kpiType) {
                    setChartData(undefined)
                    return
                }
                if (!widget.kpiType || !widget.chartType) return
                setLoadingData(true)
                latestFetchId.current += 1
                const fetchId = latestFetchId.current
                const data = await getChartData([widget], true)
                if (fetchId !== latestFetchId.current) return // wait if there is another update coming
                newWidget.data = data
                setChartData(data)
                setLoadingData(false)
            },
            {
                equals: comparer.structural,
                delay: 200, // batch updates to prevent multiple chart re-render when many fields change at once
            },
        )
        return dispose
    }, [newWidget])

    useEffect(() => {
        const { getChartData } = dashStore
        const dispose = reaction(
            () => newWidget.chartType,
            async (newVal, oldVal) => {
                const isGaugeTransition = newVal === 'gaugeNew' || oldVal === 'gaugeNew'
                if (newVal && (!oldVal || isGaugeTransition)) {
                    setLoadingData(true)
                    latestFetchId.current += 1
                    const fetchId = latestFetchId.current
                    const data = await getChartData([newWidget.newWidgetToWidget()], true)
                    if (fetchId !== latestFetchId.current) return
                    newWidget.data = data
                    setChartData(data)
                    setLoadingData(false)
                }
            },
        )
        return dispose
    }, [newWidget])
    const { questionId, formId } = newWidget.newWidgetToWidget()
    const containerKey = `${questionId}-${formId}`

    const getChartForPreview = () => {
        const chart = dashStore.getChart({
            ...chartData[0],
            title: newWidget.title,
            containerSize: { height: 400, width: 450 },
            chartType: newWidget.chartType,
            widgetRenderSettings: newWidget.widgetRenderSettings,
            kpiType: chartData[0].kpiType,
        }, chartData[0].data, Array.from(years.keys()).filter((y) => !!years.get(y)), undefined, undefined, true)
        return chart
    }

    return (
        <Panel style={{ flex: '1' }}>
            <div style={{
                display: 'flex', flexDirection: 'column', height: '100%', gap: '8px',
            }}
            >
                <div>
                    <Typography type="h3">
                        {i18n.t('dashboards.widgetPreview.title')}
                    </Typography>
                    <Typography type="p">
                        {i18n.t('dashboards.widgetPreview.desc')}
                    </Typography>
                </div>
                <div style={{
                    height: '100%', width: '100%', overflow: 'hidden', flex: '1',
                }}
                >
                    <div className="previewPane" style={{ height: '100%', width: '100%' }}>
                        {chartData && !loadingData
                            ? (
                                <WidgetContainer
                                    renderChart
                                    chart={getChartForPreview()}
                                    chartKey={newWidget.chartType}
                                    title={newWidget.title}
                                    DynamicDashboardStore={dashStore}
                                    preview
                                    changeTitle={({ value }) => {
                                        newWidget.changeNewWidgetBulk([{ value, field: 'title' }])
                                    }}
                                    key={containerKey}
                                />
                            )
                            : (
                                <div className="emptyState">
                                    <img src={EmptyStateImg} alt="Chart placeholder: not enough parameters" />
                                    <Typography type="b" style={{ textAlign: 'center' }}>
                                        {i18n.t('dashboards.widgetPreview.placeholder')}
                                    </Typography>
                                </div>
                            )}
                    </div>
                </div>
            </div>
        </Panel>
    )
}

export default observer(WidgetPreview)
